import { FETCH_QUERIES_NAME, MUTATION_KEYS } from '@common/constants';
import { AxiosErrorResponse } from '@core/error.models';
import { IProjectTeamBase } from '@projectTeam/projectTeam.models';
import { useMutation, useQuery } from '@tanstack/react-query';
import { IUploadFileRequest, IUploadFileRequestMutationProps } from '@file/file.models';
import { IAnnouncementBase, ICreateAnnouncement, IUpdateAnnouncement } from '@announcement/announcement.models';
import { isPositiveNumber } from '@core/Primitives/number.services';
import {
  ISignature,
  ISyncNoAccountUserProfileByProjectNoRequest,
  ISyncNoAccountUserProfileByProjectNoResponse,
  IUpdateMySignatureRequest,
  IUpdateDummyUserProfileRequest,
  ICreateSignatureRequest,
} from '@userProfile/userProfile.models';
import { ICreateTrainingRequest, ITraining, IUpdateTrainingRequest } from '@userProfile/training.models';
import {
  IProjectTransmittalItemRevision,
  IProjectTransmittalItemRevisionRequest,
} from '@transmittal/transmittalItemRevision/transmittalItemRevision.models';
import {
  IProjectUserProfile,
  IProjectUserProfileByCompany,
  IUpdateProjectUserProfileRequest,
} from '@userProfile/projectUserProfile.models';
import { IItemResponse } from '@gcl/react-core';
import { ICreateRoleRequest } from '@role/role.models';
import { ICreateProjectRoleRequest } from '@role/projectRole/projectRole.models';
import { ICreatePermissionRequest, ICreateRolePermissionRequest } from '@userProfile/permission/permission.models';
import {
  createAdminsTrainingAsync,
  getAdminProjectTeamsAsync,
  getAdminsAnnouncementAsync,
  getAdminsProjectUserProfileByCompany,
  getAdminsSignatureAsync,
  getAdminsTrainingAsync,
  getICECertAsync,
  postAdminsAnnouncementAsync,
  postAdminsSignatureAsync,
  postAdminsPermissionsAsync,
  postAdminsProjectRolesAsync,
  postAdminsRolePermissionsAsync,
  postAdminsRolesAsync,
  postAnnouncementFileUploadAsync,
  putAdminsAnnouncementAsync,
  putAdminsProjectUserProfile,
  putAdminsUserProfilesSyncNoAccountUsersByProjectNo,
  putUserProfilesReplaceDummyAccounts,
  syncAdminsTrainingDailyAsync,
  updateAdminProjectTeamsAsync,
  updateAdminsSignatureAsync,
  updateAdminsTrainingAsync,
  updateICECertAsync,
} from './admin.services';

// team info
export const useFetchAdminTeamInfo = (id?: number, enable = true) => {
  return useQuery<IProjectTeamBase | undefined, AxiosErrorResponse>(
    [FETCH_QUERIES_NAME.ADMIN_TEAM_INFO, id],
    async () => {
      if (!id) {
        return undefined;
      }
      const results = await getAdminProjectTeamsAsync(id);
      return results;
    },
    {
      enabled: !!id && enable,
    },
  );
};
export const useUpdateAdminTeamInfo = () => {
  return useMutation<IProjectTeamBase, AxiosErrorResponse, IProjectTeamBase>(
    [MUTATION_KEYS.UPDATE_ADMIN_TEAM_INFO],
    async (props: IProjectTeamBase) => {
      const result = await updateAdminProjectTeamsAsync(props);
      return result;
    },
  );
};

// announcement
export const useAnnouncementFileUpload = () => {
  return useMutation<string | undefined, AxiosErrorResponse, IUploadFileRequestMutationProps>(
    [MUTATION_KEYS.UPLOAD_ANNOUNCEMENT_FILE],
    async (props: IUploadFileRequestMutationProps) => {
      const request: IUploadFileRequest = {
        fileContent: props.fileContent?.files?.[0],
        fileName: props.fileContent?.files?.[0].name,
        shouldOverwrite: props.shouldOverwrite,
      };
      const result = await postAnnouncementFileUploadAsync(request);
      return result.item;
    },
  );
};

export const useFetchAnnouncement = (id?: number, enable = true) =>
  useQuery<IAnnouncementBase | undefined, AxiosErrorResponse>(
    [FETCH_QUERIES_NAME.ANNOUNCEMENT_DETAIL, id],
    async () => {
      if (!id) return undefined;
      const result = await getAdminsAnnouncementAsync(id);
      return result.item;
    },
    {
      enabled: enable && isPositiveNumber(id),
    },
  );

export const useCreateAnnouncement = () => {
  return useMutation<IAnnouncementBase | undefined, AxiosErrorResponse, ICreateAnnouncement>(
    [MUTATION_KEYS.CREATE_ANNOUNCEMENT],
    async (props: ICreateAnnouncement) => {
      const result = await postAdminsAnnouncementAsync(props);
      return result.item;
    },
  );
};
export const useUpdateAnnouncement = () => {
  return useMutation<IAnnouncementBase | undefined, AxiosErrorResponse, IUpdateAnnouncement>(
    [MUTATION_KEYS.CREATE_ANNOUNCEMENT],
    async (props: IUpdateAnnouncement) => {
      const result = await putAdminsAnnouncementAsync(props);
      return result.item;
    },
  );
};

// user profile
export const useUpdateUserProfilesSyncNoAccountUsersByProjectNo = () => {
  return useMutation<
    ISyncNoAccountUserProfileByProjectNoResponse | undefined,
    AxiosErrorResponse,
    ISyncNoAccountUserProfileByProjectNoRequest
  >(
    [MUTATION_KEYS.UPDATE_USER_PROFILE_SYNC_NO_ACCOUNT_USERS],
    async (props: ISyncNoAccountUserProfileByProjectNoRequest) => {
      const result = await putAdminsUserProfilesSyncNoAccountUsersByProjectNo(props);
      return result;
    },
  );
};

export const useAdminUpdateProjectUserProfileAsync = () => {
  return useMutation<IItemResponse<IProjectUserProfile>, AxiosErrorResponse, IUpdateProjectUserProfileRequest, unknown>(
    [MUTATION_KEYS.ADMIN_UPDATE_PROJECT_USER_PROFILE],
    async (updateRequest: IUpdateProjectUserProfileRequest) => {
      const result = await putAdminsProjectUserProfile(updateRequest);
      return result;
    },
  );
};
export const useReplaceDummyUserProfiles = () => {
  return useMutation<boolean, AxiosErrorResponse, IUpdateDummyUserProfileRequest>(
    [MUTATION_KEYS.REPLACE_DUMMY_USER_PROFILES],
    async (props: IUpdateDummyUserProfileRequest) => {
      const result = await putUserProfilesReplaceDummyAccounts(props);
      return result;
    },
  );
};

// training

export const useFetchTraining = (id?: number, enable = true) =>
  useQuery<ITraining | undefined, AxiosErrorResponse>(
    [FETCH_QUERIES_NAME.TRAINING, id],
    async () => {
      if (!id) return undefined;
      const result = await getAdminsTrainingAsync(id);
      return result.item;
    },
    {
      enabled: enable && isPositiveNumber(id),
    },
  );

export const useCreateTraining = () => {
  return useMutation<ITraining | undefined, AxiosErrorResponse, ICreateTrainingRequest>(
    [MUTATION_KEYS.CREATE_TRAINING],
    async (props: ICreateTrainingRequest) => {
      const result = await createAdminsTrainingAsync(props);
      return result.item;
    },
  );
};
export const useUpdateTraining = () => {
  return useMutation<ITraining | undefined, AxiosErrorResponse, IUpdateTrainingRequest>(
    [MUTATION_KEYS.UPDATE_TRAINING],
    async (props: IUpdateTrainingRequest) => {
      const result = await updateAdminsTrainingAsync(props);
      return result.item;
    },
  );
};

export const useSyncTraining = () => {
  return useMutation<boolean, AxiosErrorResponse>([MUTATION_KEYS.SYNC_TRAINING], async () => {
    const result = await syncAdminsTrainingDailyAsync();
    return result;
  });
};

// ICE cert
export const useFetchICECert = (id?: number, enable = true) => {
  return useQuery<IProjectTransmittalItemRevision | undefined, AxiosErrorResponse>(
    [FETCH_QUERIES_NAME.ICE_CERT, id],
    async () => {
      if (!id) {
        return undefined;
      }
      const result = await getICECertAsync(id);
      return result;
    },
    {
      enabled: !!id && enable,
    },
  );
};

export const useUpdateICECert = () => {
  return useMutation<
    IProjectTransmittalItemRevision | undefined,
    AxiosErrorResponse,
    IProjectTransmittalItemRevisionRequest
  >([MUTATION_KEYS.UPDATE_ICE_CERT], async (props: IProjectTransmittalItemRevisionRequest) => {
    const result = await updateICECertAsync(props);
    return result.item;
  });
};

// project user profile
export interface IFetchProjectUserProfileByCompanyRequest {
  companyId: number;
  projectId: number;
}
export const useFetchProjectUserProfileByCompany = (
  id?: number,
  enable = true,
  props: IFetchProjectUserProfileByCompanyRequest | undefined = undefined,
) => {
  return useQuery<IProjectUserProfileByCompany | undefined, AxiosErrorResponse>(
    [FETCH_QUERIES_NAME.ADMIN_PROJECT_USER_PROFILE_WITH_ROLES_BY_COMPANY, id],
    async () => {
      if (!id) {
        return undefined;
      }
      const result = await getAdminsProjectUserProfileByCompany(id, props);
      return result.item;
    },
    {
      enabled: !!id && enable,
    },
  );
};

// signature

export const useFetchAdminSignature = (id?: number, enable = true) => {
  return useQuery<ISignature | undefined, AxiosErrorResponse>(
    [FETCH_QUERIES_NAME.ADMIN_SIGNATURE, id],
    async () => {
      if (!id) {
        return undefined;
      }
      const result = await getAdminsSignatureAsync(id);
      return result.item;
    },
    {
      enabled: !!id && enable,
    },
  );
};
// roles & permissions
export const useCreateRole = () => {
  return useMutation<boolean, AxiosErrorResponse, ICreateRoleRequest>(
    [MUTATION_KEYS.CREATE_ROLE],
    async (props: ICreateRoleRequest) => {
      const result = await postAdminsRolesAsync(props);
      return result;
    },
  );
};
export const useCreateProjectRole = () => {
  return useMutation<boolean, AxiosErrorResponse, ICreateProjectRoleRequest>(
    [MUTATION_KEYS.CREATE_PROJECT_ROLE],
    async (props: ICreateProjectRoleRequest) => {
      const result = await postAdminsProjectRolesAsync(props);
      return result;
    },
  );
};

export const useUpdateAdminSignature = () => {
  return useMutation<ISignature | undefined, AxiosErrorResponse, IUpdateMySignatureRequest>(
    [MUTATION_KEYS.ADMIN_UPDATE_SIGNATURE],
    async (props: IUpdateMySignatureRequest) => {
      const result = await updateAdminsSignatureAsync(props);
      return result.item;
    },
  );
};

export const usePostAdminSignature = () => {
  return useMutation<ISignature | undefined, AxiosErrorResponse, ICreateSignatureRequest>(
    [MUTATION_KEYS.ADMIN_CREATE_SIGNATURE],
    async (props: ICreateSignatureRequest) => {
      const result = await postAdminsSignatureAsync(props);
      return result.item;
    },
  );
};
export const useCreatePermission = () => {
  return useMutation<boolean, AxiosErrorResponse, ICreatePermissionRequest>(
    [MUTATION_KEYS.CREATE_PERMISSION],
    async (props: ICreatePermissionRequest) => {
      const result = await postAdminsPermissionsAsync(props);
      return result;
    },
  );
};
export const useCreateRolePermission = () => {
  return useMutation<boolean, AxiosErrorResponse, ICreateRolePermissionRequest>(
    [MUTATION_KEYS.CREATE_ROLE_PERMISSION],
    async (props: ICreateRolePermissionRequest) => {
      const result = await postAdminsRolePermissionsAsync(props);
      return result;
    },
  );
};
